import { Bell, Info, AlertTriangle, CheckCircle, XCircle, X } from 'lucide-react';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import type { Notification } from '@/types';

interface NotificationPanelProps {
  notifications: Notification[];
  onClose: () => void;
  onMarkAsRead: (notificationId: string) => Promise<void>;
  onMarkAllAsRead: () => Promise<void>;
}

export function NotificationPanel({ 
  notifications, 
  onClose, 
  onMarkAsRead, 
  onMarkAllAsRead 
}: NotificationPanelProps) {
  const unreadCount = notifications.filter((n) => !n.read).length;

  const getIcon = (type: string) => {
    switch (type) {
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Bell className="w-5 h-5 text-gray-500" />;
    }
  };

  if (notifications.length === 0) return null;

  return (
    <>
      {/* オーバーレイ */}
      <div className="fixed inset-0 bg-black bg-opacity-25 z-40" onClick={onClose}></div>

      {/* パネル */}
      <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl z-50 border border-gray-200 max-h-[600px] flex flex-col">
        {/* ヘッダー */}
        <div className="flex justify-between items-center p-4 border-b bg-gradient-to-r from-blue-50 to-blue-100">
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-blue-600" />
            <h3 className="font-bold text-gray-800">お知らせ</h3>
            {unreadCount > 0 && (
              <span className="inline-flex items-center justify-center w-6 h-6 text-xs font-bold text-white bg-red-500 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={onMarkAllAsRead}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                すべて既読
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* 通知リスト */}
        <ul className="divide-y overflow-y-auto flex-1">
          {notifications.length === 0 ? (
            <li className="p-8 text-center text-gray-500">
              <Bell className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>お知らせはありません</p>
            </li>
          ) : (
            notifications.map((notif) => (
              <li
                key={notif.id}
                onClick={() => !notif.read && onMarkAsRead(notif.id)}
                className={`p-4 cursor-pointer transition-colors ${
                  !notif.read
                    ? 'bg-blue-50 hover:bg-blue-100'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">{getIcon(notif.type || 'info')}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-1">
                      <p
                        className={`text-sm font-semibold ${
                          !notif.read ? 'text-gray-900' : 'text-gray-700'
                        }`}
                      >
                        {notif.title}
                      </p>
                      {!notif.read && (
                        <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0"></div>
                      )}
                    </div>
                    <div className="text-sm text-gray-600 mb-2 prose prose-sm max-w-none">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          a: ({ node, ...props }) => (
                            <a
                              {...props}
                              className="text-blue-600 hover:text-blue-800 underline"
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            />
                          ),
                          p: ({ node, ...props }) => <p {...props} className="inline" />,
                        }}
                      >
                        {notif.message}
                      </ReactMarkdown>
                    </div>
                    {notif.showDate !== false && (
                      <p className="text-xs text-gray-400">
                        {format(notif.createdAt.toDate(), 'yyyy/MM/dd HH:mm', { locale: ja })}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>
    </>
  );
}
